"use client";
import React from "react";
import { useQuery } from "react-query";
import { useState, useCallback, useMemo } from "react";
import { LiveResponseItem } from "@/interfaces/live-response.interface";
import useLiveResponseService from "@/services/live-response-service";
import AdminHeader from "@/components/header/admin-header";
import { ADMIN_PANEL_HEADER } from "@/constants/admin.constant";
import { DataTable, ColumnDef } from "@/components/ui/data-table";
import { Rating } from "@/components/ui/rating";
import { RotateCcw, ChevronDown, ChevronUp, MessageCircleMore, Loader2, FlaskConical, Info } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

/**
 * LiveResponseWrapper component is the main wrapper for the live response
 * evaluation page. Manages live response data fetching, pagination, and displays
 * responses in a tabbed interface with rating metrics and re-analysis functionality.
 * 
 * @component
 * @returns {JSX.Element} Live response page with tabs and data table
 * 
 * @example
 * ```tsx
 * <LiveResponseWrapper />
 * ```
 */
export const LiveResponseWrapper = () => {
  const { getLiveResponses } = useLiveResponseService();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);

  const {
    data: responseData,
    isLoading,
    isFetching,
    refetch,
  } = useQuery(
    ["liveResponses", page, pageSize],
    () => getLiveResponses(page, pageSize),
    {
      enabled: true,
      refetchOnMount: true,
    }
  );

  const allData: LiveResponseItem[] = responseData?.data || [];
  const pagination = responseData?.pagination;

  const handleReAnalyse = useCallback(async () => {
    refetch();
  }, [refetch]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  }, []);

  const truncateText = (text: string, maxLength: number = 80): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const renderQuery = (item: LiveResponseItem): React.ReactNode => {
    const truncatedResponse = truncateText(item.query, 80);
    const isTruncated = item.query.length > 80;
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1">
          <span className="font-medium text-foreground">{isTruncated ? truncatedResponse : item.query}</span>
        </div>
        <span className="text-xs text-muted-foreground mt-1">
          model={item.model} response_time={item.responseTime}s
        </span>
      </div>
    );
  };

  const renderResponse = (item: LiveResponseItem): React.ReactNode => {
    const truncatedResponse = truncateText(item.response, 80);
    const isTruncated = item.response.length > 80;

    return (
      <div className="flex flex-col h-full">
        <div className="flex-1">
          <span className="text-foreground">
            {isTruncated ? truncatedResponse : item.response}
          </span>
        </div>
      </div>
    );
  };

  const renderMetrics = (averageScore: number): React.ReactNode => {
    return <Rating value={averageScore} max={5} />;
  };

  const renderUserFeedback = (feedback: string | null): React.ReactNode => {
    if (!feedback) {
      return <span className="text-muted-foreground">Not provided</span>;
    }
    return <span className="text-foreground">{feedback}</span>;
  };

  const renderExpandedContent = (item: LiveResponseItem): React.ReactNode => {
    return (
      <div className="p-6 border-t">
        <div className="flex gap-5">
          {/* Full Response Section */}
          <div className="w-3/4">
            <Tabs defaultValue="response">
              <div className="flex justify-between items-center">
                <TabsList>
                  <TabsTrigger value="query">Query</TabsTrigger>
                  <TabsTrigger value="response">Response</TabsTrigger>
                </TabsList>
                {item.latency && (
                  <span className="text-sm text-muted-foreground">
                    Latency - {item.latency}s
                  </span>
                )}
              </div>
              <TabsContent value="query">
                <div className="bg-gray-100 rounded-md p-4 overflow-y-auto h-48 relative">
                  <p className="text-foreground leading-relaxed text-sm absolute">{item.query}</p>
                </div>
              </TabsContent>
              <TabsContent value="response">
                <div className="bg-gray-100 rounded-md p-4 overflow-y-auto h-48 relative">
                  <p className="text-foreground leading-relaxed text-sm absolute">{item.response}</p>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Metrics Details */}
          <div className="w-1/4">
            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <FlaskConical className="h-5 w-5 text-foreground" />
                  <h3 className="font-semibold text-foreground">Metrics</h3>
                </div>
                <Popover>
                  <PopoverTrigger>
                    <Info className="h-4 w-4" />
                  </PopoverTrigger>
                  <PopoverContent side="left" align="end" className="min-w-[500px]">
                    <div className="flex flex-col gap-3 pb-1">
                      <div>
                        <div className="font-medium text-sm">Clarity (Hallucination)</div>
                        <div className="font-secondary mt-1 text-sm text-foreground">Checks whether the response stays clear and factual without introducing invented or unsupported information.</div>
                      </div>
                      <div>
                        <div className="font-medium text-sm">Faithfulness</div>
                        <div className="font-secondary mt-1 text-sm text-foreground">Measures how strictly the response stays true to the given source context without distortion or exaggeration.</div>
                      </div>
                      <div>
                        <div className="font-medium text-sm">Coherence</div>
                        <div className="font-secondary mt-1 text-sm text-foreground">Evaluates how logically connected and easy to follow the response is from start to finish.</div>
                      </div>
                      <div>
                        <div className="font-medium text-sm">Context Relevancy</div>
                        <div className="font-secondary mt-1 text-sm text-foreground">Measures how effectively the response uses the provided context without adding unrelated information.</div>
                      </div>
                      <div>
                        <div className="font-medium text-sm">Answer Relevancy</div>
                        <div className="font-secondary mt-1 text-sm text-foreground">Measures how closely the model's response aligns with the user's question.</div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-3">
                {Object.entries(item.metrics).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-3">
                    <span className="text-sm font-medium text-foreground capitalize min-w-[100px]">
                      {key}
                    </span>
                    <div className="flex-1 flex items-center gap-3">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[200px]">
                        <div
                          className="bg-primary-500 h-2 rounded-full"
                          style={{ width: `${(value / 5) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm text-foreground min-w-[2.5rem] text-right">
                        {value.toFixed(1)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const columns: ColumnDef<LiveResponseItem>[] = [
    {
      id: "query",
      header: "Query",
      accessor: (row) => row,
      render: (item) => renderQuery(item),
      sortable: false,
      headerClassName: "min-w-[300px]",
    },
    {
      id: "response",
      header: "Response",
      accessor: (row) => row,
      render: (item) => renderResponse(item),
      sortable: false,
      headerClassName: "min-w-[300px]",
    },
    {
      id: "metrics",
      header: "Metrics",
      accessor: (row) => row.averageScore,
      render: (value) => renderMetrics(value),
      sortable: false,
      headerClassName: "min-w-[150px]",
    },
    {
      id: "userFeedback",
      header: "User Feedback",
      accessor: (row) => row.userFeedback,
      render: (feedback) => renderUserFeedback(feedback),
      sortable: false,
      headerClassName: "min-w-[200px]",
    },
  ];

  return (
    <div className="h-full flex flex-col bg-background text-text-500 px-4 pb-4">
      <div className="flex justify-between">
        <AdminHeader header={ADMIN_PANEL_HEADER.LIVE_RESPONSE_MONITOR} />
        <div className="flex justify-end">
          <button
            onClick={handleReAnalyse}
            disabled={isFetching}
            className="font-medium text-[#005587] flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4" />
            )}
            Refresh Data
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className="mt-6 flex-1">
        <DataTable
          data={allData}
          columns={columns}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          isLoading={isLoading}
          emptyMessage="No response data available"
          getRowKey={(row) => row.id}
          enableExpand={true}
          renderExpandedContent={renderExpandedContent}
        />
      </div>
    </div>
  );
};

